import React, { useEffect, useRef, useState } from 'react';
import { history, useParams, useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { Icon, Modal, message, Button } from '@/components';

import UnReadChatPopover from './PC/Chat/UnReadChatPopover';
import CameraBtn from './PC/Camera';
import ShareScreenBtn from './PC/ShareScreen';
import Member from './PC/Member';
import LeaveBtn from './PC/Leave';
import SettingBtn from './PC/Setting';
import s from './pc.less';
import { RootStore, common, transcribe } from '@/models';
import ApplicationsBtn from './PC/Applications';
import ChatBtn from './PC/Chat';
import FeedbackBtn from './PC/Feedback';
import SimultaneousInterpretationBtn from './PC/SimultaneousInterpretation';
import SpeakerBtn from './PC/Speaker';
import BarrageBtn from './PC/BarrageBtn';
import ReactionBtn from './PC/ReactionBtn';
import TranscribeBtn from './PC/TranscribeBtn';
import { elkLog } from '@/utils/elkReport';
import MicrophoneBtn from './PC/Microphone';
import RecordingBtn from './PC/Recording';
import classNames from 'classnames';
import { motion, useAnimationControls } from 'framer-motion';
import { useDebounce, useDebounceFn, useMouse } from 'ahooks';
import { ShareScreenFull } from '@/models/meeting';

interface ControlProps extends Partial<Pick<RootStore, 'chat' | 'meeting' | 'user'>> {
  RTCInstance?: RootStore['rtc'];
  simultaneous?: RootStore['simultaneousInterpretation'];
}
interface ParamsStruct {
  meetingId: string;
}

const ControlBar: React.FC<ControlProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    RTCInstance: store.rtc,
    chat: store.chat,
  };
})(
  observer((props) => {
    const { meeting, RTCInstance, chat } = props;
    const [isChatPopoverVisible, setIsChatPopoverVisible] = React.useState(false);
    const { meetingId } = useParams<ParamsStruct>();
    const { formatMessage } = useIntl();
    const controller = useAnimationControls();
    const mouse = useMouse();
    const [upAnimate, setUpAnimate] = useState(false);
    const [downAnimate, setDownAnimate] = useState(false);
    const [isDown, setIsDown] = useState<boolean>(false);

    const firstFullScreenRef = useRef<any>(false);
    const stopScreenClick = React.useCallback(async () => {
      Modal.confirm({
        focusTriggerAfterClose: false,
        title: formatMessage({ id: 'ARE_YOU_SURE_YOU_WANT_TO_STOP_SCREEN_SHARING' }),
        okText: formatMessage({ id: 'STOP' }),
        cancelText: formatMessage({ id: 'CANCEL' }),
        okButtonProps: {
          className: `popo-meeting-button`,
          danger: true,
        },
        onOk: async () => {
          await RTCInstance?.setIsShareScreen(false);
        },
      });
    }, [RTCInstance]);

    React.useEffect(() => {
      if (
        !RTCInstance?.ableDoubleScreem &&
        RTCInstance?.isOpenVideo &&
        RTCInstance?.isShareScreen
      ) {
        message.info({
          content: formatMessage({ id: 'INPUT_OLD_CLOSE_CARAME' }),
          noIcon: true,
        });
        RTCInstance?.closeCamera();
      }
    }, [RTCInstance?.ableDoubleScreem, RTCInstance?.isOpenVideo, RTCInstance?.isShareScreen]);

    useEffect(() => {
      if (RTCInstance?.rtcTrackEnded) {
        Modal.info({
          title: formatMessage({ id: 'DEVICE_IS_ABNORMAL' }),
          okText: formatMessage({ id: 'REFRESH' }),
          okButtonProps: {
            className: `btn-blue`,
          },
          onOk() {
            history.go(0);
          },
        });
      }
    }, [RTCInstance?.rtcTrackEnded]);

    const chatPanelBtnClick = React.useCallback(
      (e, locateMsg = false) => {
        if (RTCInstance?.breakLineReconnecting) {
          return;
        }
        if (!chat?.isAbleUse) return;
        setIsChatPopoverVisible(false);
        // e.nativeEvent.stopImmediatePropagation();
        chat?.setChatVisible(!chat?.chatVisible, locateMsg);
        chat?.chatVisible && meeting?.setPeoplePanelShow(false);
        chat?.chatVisible &&
          meeting?.shareScreenFull === ShareScreenFull.FULL &&
          meeting?.setShareScreenFull(ShareScreenFull.EXIT_FULL);
        transcribe?.transcribeVisible && transcribe?.setTranscribeVisible(false);
      },
      [chat, RTCInstance]
    );

    const handleAddAniamteOpenNoteBtn = () => {
      const openNoteBtn = document.getElementById('openNoteBtn');
      openNoteBtn?.classList.add('moveDown');
      openNoteBtn?.classList.remove('moveUp');
    };

    const handleRemoveAniamteOpenNoteBtn = () => {
      const openNoteBtn = document.getElementById('openNoteBtn');
      openNoteBtn?.classList.remove('moveDown');
      openNoteBtn?.classList.add('moveUp');
    };
    const { run: animate } = useDebounceFn(
      () => {
        if (document.documentElement.clientHeight - mouse.clientY < 100) {
          console.log('====up');
          if (upAnimate || !isDown) {
            return;
          }
          if (meeting?.shareScreenFull === ShareScreenFull.FULL) {
            controller.set({
              visibility: 'visible',
            });
            controller
              .start({
                y: 0,
                transition: { delay: 0, duration: 0.4 },
                name: 'up',
              })
              .then(() => {
                setIsDown(false);
              });
            handleRemoveAniamteOpenNoteBtn();
          }
        } else {
          if (meeting?.shareScreenFull === ShareScreenFull.FULL) {
            if (downAnimate !== false || isDown) {
              return;
            }
            console.log('====down');
            controller
              .start({
                y: 60,
                transition: { delay: 2, duration: 0.4 },
                name: 'down',
              })
              .then(() => {
                controller.set({
                  visibility: 'hidden',
                });
                setIsDown(true);
              });
            handleAddAniamteOpenNoteBtn();
          }
        }
      },
      { wait: 200 }
    );

    useEffect(() => {
      if (meeting?.shareScreenFull === ShareScreenFull.FULL) {
        animate();
      }
    }, [mouse.clientY, meeting?.shareScreenFull]);

    const onAnimationStart = (evt: any) => {
      if (evt.name === 'up') {
        setUpAnimate(true);
      }
      if (evt.name === 'down') {
        setDownAnimate(true);
      }
    };

    const onAnimationComplete = (evt: any) => {
      if (evt.name === 'up') {
        setUpAnimate(false);
      }
      if (evt.name === 'down') {
        setDownAnimate(false);
      }
    };

    useEffect(() => {
      if (meeting?.shareScreenFull === ShareScreenFull.FULL) {
        setIsDown(true);
        firstFullScreenRef.current = true;
        controller.start({
          y: 132,
          transition: { delay: 2, duration: 0.4 },
          name: 'down',
        });
        setTimeout(() => {
          handleAddAniamteOpenNoteBtn();
        }, 50);
      } else {
        controller
          .start({
            y: 0,
            visibility: 'visible',
            transition: { duration: 0 },
          })
          .then(() => {
            setIsDown(false);
          });
        handleRemoveAniamteOpenNoteBtn();
      }
    }, [meeting?.shareScreenFull]);

    return (
      <>
        {RTCInstance?.isShareScreen && (
          <div className={s.screenSharingBar}>
            <span>{formatMessage({ id: 'YOU_ARE_SHARING_THE_SCREEN' })}</span>
            <Button onClick={stopScreenClick} type="primary" danger className={s.stop}>
              <Icon name="icon-endmeeting_nor" className={s.icon} />
              {formatMessage({ id: 'STOP_SHARING' })}
            </Button>
          </div>
        )}
        <motion.div
          animate={controller}
          onAnimationStart={onAnimationStart}
          onAnimationComplete={onAnimationComplete}
          className={classNames(
            s.controlBar,
            meeting?.shareScreenFull === ShareScreenFull.FULL ? s.fullScreen : ''
          )}
        >
          <div className={s.left}>
            <MicrophoneBtn />
            <CameraBtn />
          </div>
          <div className={s.controlGroup}>
            <ShareScreenBtn />
            <Member></Member>
            <SettingBtn />
            <UnReadChatPopover
              onPopoverVisibleChange={setIsChatPopoverVisible}
              popoverVisible={isChatPopoverVisible && !chat?.chatVisible}
              onClick={(e) => {
                chatPanelBtnClick(e, true);
              }}
            >
              <ChatBtn
                onClick={(e) => {
                  elkLog('点击聊天按钮');
                  chatPanelBtnClick(e, false);
                }}
              />
            </UnReadChatPopover>
            <ReactionBtn />
            <BarrageBtn />
            <RecordingBtn />
            <TranscribeBtn />
            <SimultaneousInterpretationBtn />
            <SpeakerBtn />
            <ApplicationsBtn />
            <FeedbackBtn />
          </div>
          <div className={s.right}>
            <LeaveBtn meetingId={meetingId} />
          </div>
        </motion.div>
      </>
    );
  })
);

export default ControlBar;
